import {
    DestroyRef,
    Injectable,
    InjectionToken,
    computed,
    inject,
    makeEnvironmentProviders,
    signal,
    type EnvironmentProviders,
    type Signal,
    type TemplateRef,
} from '@angular/core';

export const APP_TOP_BAR_ELIGIBILITY = new InjectionToken<Signal<boolean>>(
    'APP_TOP_BAR_ELIGIBILITY'
);

export type AppTopBarCloseReason =
    | 'detach'
    | 'eligibility-change'
    | 'escape'
    | 'navigation'
    | 'outside'
    | 'scope-change'
    | 'tab'
    | 'toggle';

export interface AppTopBarShortcut {
    readonly key: string;
    readonly label: string;
    readonly route: string | readonly string[];
    readonly icon: string;
    readonly description?: string;
}

export interface AppTopBarModuleMenuContext {
    readonly close: (reason: AppTopBarCloseReason) => void;
    readonly shortcuts: readonly AppTopBarShortcut[];
}

export interface AppTopBarBuildingIdentity {
    readonly id: string;
    readonly name: string;
    readonly address: string;
}

export type AppTopBarScopeKind = 'app' | 'building' | 'company';

interface AppTopBarScopePortalBase {
    readonly moduleMenu: TemplateRef<AppTopBarModuleMenuContext>;
    readonly moduleMenuAriaLabel: string;
    readonly moduleTriggerClassName?: string;
    readonly moduleTriggerIcon: string;
    readonly moduleTriggerLabel: string;
}

export type AppTopBarScopePortal =
    | (AppTopBarScopePortalBase & {
          readonly kind: 'app';
          readonly currentBuilding: null;
      })
    | (AppTopBarScopePortalBase & {
          readonly kind: 'building';
          readonly currentBuilding: AppTopBarBuildingIdentity;
      })
    | (AppTopBarScopePortalBase & {
          readonly kind: 'company';
          readonly currentBuilding: null;
      });

export interface AppTopBarScopeRegistration {
    destroy(): void;
}

interface AppTopBarScopeEntry {
    readonly token: symbol;
    readonly scope: Signal<AppTopBarScopePortal | null>;
}

@Injectable()
export class AppTopBarScopeRegistry {
    private readonly entries = signal<readonly AppTopBarScopeEntry[]>([]);

    public readonly activeScope = computed(() => {
        const entries = this.entries();
        return entries[entries.length - 1]?.scope() ?? null;
    });

    public register(
        scope: Signal<AppTopBarScopePortal | null>
    ): AppTopBarScopeRegistration {
        const token = Symbol('app-top-bar-scope');
        this.entries.update((entries) => [...entries, { token, scope }]);

        return {
            destroy: () => {
                this.entries.update((entries) =>
                    entries.filter((entry) => entry.token !== token)
                );
            },
        };
    }
}

export function injectAppTopBarEligibility(): Signal<boolean> {
    return inject(APP_TOP_BAR_ELIGIBILITY);
}

/** Provides one registry for an authenticated route tree. */
export function provideAppTopBarScopeRegistry(): EnvironmentProviders {
    return makeEnvironmentProviders([AppTopBarScopeRegistry]);
}

/** Registers the current route owner and tears down only its registration. */
export function provideAppTopBarScope(
    scope: Signal<AppTopBarScopePortal | null>
): void {
    const registry = inject(AppTopBarScopeRegistry);
    const destroyRef = inject(DestroyRef);
    const registration = registry.register(scope);

    destroyRef.onDestroy(() => registration.destroy());
}
